import React from 'react';
// import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../Layout';
import { clearToken } from '../../apis/Tools';
import { Tooltip, Spin } from 'antd';
import { useVisibleVistas } from '../../hooks/useVisibleVistas';
import { filterMenuByPermissions, resolveMenuPathForKey } from '../../utils/menuPermissionFilter';
import { 
  HomeOutlined, 
  UserOutlined, 
  SettingOutlined, 
  MailOutlined,
  DollarOutlined,
  TeamOutlined,
  DatabaseOutlined,
  ToolOutlined,
  FolderOpenOutlined,
  ApiOutlined,
  CalculatorOutlined,
  FileTextOutlined,
  FormOutlined,
  SafetyCertificateOutlined,
  SolutionOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  FolderOutlined,
  AppstoreOutlined,
  BankOutlined,
} from '@ant-design/icons';
import userRoles from '@app/constants/userRoles';
import { kebabCase } from 'lodash';
import { key } from 'styled-theme';
import { SHOW_ASISTENCIA_MENU } from '../../config';


const formatMenuItems = (menuItems, visibleVistas) => {
  return (menuItems || []).map((item) => {
    const newItem = { ...item };

    // Fallback icon
    if (!newItem.icon) {
      newItem.icon = <FolderOutlined />;
    }

    if (typeof newItem.label === 'string') {
      const rawLabel = newItem.label;

      const expandedLabel = (
        <span
          style={{
            display: 'inline-block',
            wordBreak: 'normal',
            whiteSpace: 'normal',
            lineHeight: '1.0',
            padding: '9px 0',
          }}
          className={kebabCase(`menu-item-expanded-text`)}
        >
          {rawLabel}
        </span>
      );

      newItem.label = expandedLabel;
    }

    if (newItem.children?.length) {
      newItem.children = formatMenuItems(newItem.children, visibleVistas);
      // Si un grupo queda sin hijos, se oculta para evitar menús vacíos.
      if (!newItem.children.length) {
        return null;
      }
    } else if (typeof newItem.key === 'string') {
      const resolvedPath = resolveMenuPathForKey(newItem.key, visibleVistas);
      if (resolvedPath) {
        newItem.key = resolvedPath;
      } else {
        // Hoja sin ruta resoluble -> ocultar para evitar navegar a /home/<key>.
        return null;
      }
    }

    return newItem;
  }).filter(Boolean);
};

/**
 * Retorna la estructura completa del menú (misma para todos los roles)
 * La diferencia entre roles viene del filtrado por permisos en lugar de cambiar la estructura
 */
const getFullMenuStructure = () => {
  return [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: 'Inicio',
    },
    {
      key: 'configuration',
      label: 'Configuración',
      icon: <SettingOutlined />,
      children: [
        {
          key: 'general',
          label: 'General',
          children: [
            {
              key: 'configuracion_aplicacion',
              label: 'Configuración de la Aplicación',
            },
            {
              key: 'companias',
              icon: <BankOutlined />,
              label: 'Compañías',
            },
            {
              key: 'users',
              icon: <UserOutlined />,
              label: 'Usuarios',
            },
            {
              key: 'roles',
              icon: <TeamOutlined />,
              label: 'Roles',
            },
            {
              key: 'configuracion_email',
              icon: <MailOutlined />,
              label: 'Configuración de Correo',
            },
          ]
        },
        {
          key: 'nomina_sec',
          label: 'Nomina',
          children: [
            {
              key: 'configuracion_nomina',
              label: 'Configuración de Nómina',
            },
            {
              key: 'parametros',
              label: 'Parametros',
            },
            {
              key: 'tipo_concepto',
              label: 'Tipos de Conceptos',
            },
            {
              key: 'tipo_saldo',
              label: 'Tipos de Saldos',
            },
            {
              key: 'concepto',
              label: 'Conceptos',
            },
            {
              key: 'concepto_acomulado',
              label: 'Acumulados',
            },
            {
              key: 'periodos',
              label: 'Periodos',
            },
            {
              key: 'calculos',
              label: 'Calculos',
            },
            {
              key: 'cuentas_contables',
              label: 'Cuentas Contables',
            },
            {
              key: 'formulas',
              label: 'Formulas',
              children: [
                {
                  key: 'variable_formula',
                  label: 'Variables de Formulas',
                },
                {
                  key: 'formulas_globales',
                  label: 'Formulas Globales',
                },

                {
                  key: 'funciones',
                  label: 'Funciones',
                },
              ]
            },
            {
              key: 'interfases',
              label: 'Interfaces',
              children: [
                {
                  key: 'definicion_polizas',
                  label: 'Definicion de Polizas',
                },
                {
                  key: 'definicion_formato_banco',
                  label: 'Definicion Formato Banco',
                }
              ]
            }
          ]
        },
        {
          key: 'recursoshumanos',
          label: 'Recursos Humanos',
          children: [
            {
              key: 'certificados',
              label: 'Certificados',
            },
            {
              key: 'registro-patronal',
              label: 'Registro Patronal',
            },
            {
              key: 'areas-trabajo',
              label: 'Áreas de Trabajo',
            },
            {
              key: 'tabuladores',
              label: 'Tabuladores',
            },
            {
              key: 'rutas',
              label: 'Rutas',
            },
            {
              key: 'integrados',
              label: 'Integración',
            },
            {
              key: 'niveles',
              label: 'Niveles de Organización',
              children: [
                {
                  key: 'niveles-organizacionales',
                  label: 'Nivel 1',
                }, {
                  key: 'niveles-organizacionales2',
                  label: 'Nivel 2',
                },
                {
                  key: 'niveles-organizacionales3',
                  label: 'Nivel 3',
                },
                {
                  key: 'niveles-organizacionales4',
                  label: 'Nivel 4',
                },
                {
                  key: 'niveles-organizacionales5',
                  label: 'Nivel 5',
                },
              ],
            },
            {
              key: 'motivos_baja',
              label: 'Motivos de Baja',
            },
            {
              key: 'tipos_liquidacion',
              label: 'Tipos de Liquidación',
            },
            {
              key: 'horarios',
              label: 'Horarios',
            },
            {
              key: 'supervisores',
              label: 'Supervisores',
            },
            {
              key: 'motivos',
              label: 'Motivos',
            },
            {
              key: 'tipos_ausencia',
              label: 'Tipos de ausencia',
            },
            {
              key: 'tipo_incapacidad',
              label: 'Tipos de Incapacidad',
            }
          ],
        },
        ...(SHOW_ASISTENCIA_MENU ? [{
          key: 'asistencia_configuracion',
          label: 'Asistencia',
          icon: <CalendarOutlined />,
          children: [
            {
              key: 'dias_festivos',
              label: 'Días Festivos'
            },
            {
              key: 'estacion_chequeo',
              label: 'Estaciones de Chequeo',
            },
            {
              key: 'configuracion_checadas',
              label: 'Configuración de Checadas'
            },
          ],
        }] : []),
      ],
    },
    {
      key: 'nomina',
      label: 'Nomina',
      icon: <DollarOutlined />,
      children: [
        {
          key: 'empleado_nomina',
          label: 'Empleados',
          icon: <UserOutlined />,
          children: [
            {
              key: 'historico_nomina_empleado',
              label: 'Historico de nomina'
            },
            {
              key: 'acumulados_del_periodo',
              label: 'Acumulados del Periodo',
            },
            {
              key: 'timbrados_del_periodo',
              label: 'Timbrados del Período'
            },
            {
              key: 'nomina_cerrada',
              label: 'Acumulados por empleado',
            },
            {
              key: 'corregir_acumulados',
              label: 'Correción de Acumulados',
              children: [
                {
                  key: 'correcion_acumulado_mensual',
                  label: 'Mensuales',
                },
                {
                  key: 'correcion_acumulado_anual',
                  label: 'Anuales',
                },
              ],
            },
            {
              key: 'generar_poliza',
              label: 'Generar Poliza',
            },
          ]
        },
        {
          key: 'captura_nomina',
          label: 'Capturar',
          icon: <DatabaseOutlined />,
          children: [
            {
              key: 'saldos-empleado',
              label: 'Saldos Empleado',
              icon: <CreditCardOutlined />,
            },
            {
              key: 'movimientos-company',
              label: 'Movimientos Compañia',
            },
            {
              key: 'movimientos-empleado',
              label: 'Movimientos Empleados',
            },
            {
              key: 'movimientos-dias-horas',
              label: 'Movimientos Dias/Horas',
            }
          ]
        },
        {
          key: 'procesos_nomina',
          label: 'Procesos',
          icon: <ToolOutlined />,
          children: [
            {
              key: 'procesar_nomina',
              label: 'Nomina Individual',
            },
            {
              key: 'procesar_nomina_global',
              label: 'Nomina Global',
            },
            {
              key: 'cierre_nomina',
              label: 'Cierre',
            },
            {
              key: 'timbrar_nomina',
              label: 'Timbrar Nomina',
            },
            {
              key: 'timbrar_periodo',
              label: 'Timbrar Periodo',
            },
            {
              key: 'desafectar_nomina',
              label: 'Desafectar Nomina',
            },

            {
              key: 'distribucion_contable',
              label: 'Distribución Contable',
            },
            {
              key: 'depuracion_isr',
              label: 'Depuración de ISR',
            },
            {
              key: 'piramidacion',
              label: 'Piramidación',
            },
            {
              key: 'retroactivo',
              label: 'Retroactivo',
            },
          ]
        },
        {
          key: 'liquidacion',
          label: 'Liquidación',
          icon: <FolderOpenOutlined />,
          children: [
            {
              key: 'finiquitos',
              label: 'Finiquitos'
            },
            {
              key: 'recibo_finiquito',
              label: 'Recibo Finiquito'
            }
          ]
        },
        {
          key: 'interfases',
          label: 'Interfaces',
          icon: <ApiOutlined />,
          children: [
            {
              key: 'distribucion_bancaria',
              label: 'Distribución Bancaria'
            },
            {
              key: 'envio_recibos_correo',
              label: 'Envío de Recibos por Correo'
            },
            {
              key: 'importacion_saldos',
              label: 'Importación de Saldos'
            },
            {
              key: 'inicial',
              label: 'Inicial',
              icon: <FolderOpenOutlined />,
              children: [
                {
                  key: 'importacion_acumulados',
                  label: 'Importación de Acumulados'
                },
                {
                  key: 'importacion_nomina',
                  label: 'Importación Nómina'
                },
                {
                  key: 'importacion_formato_poliza',
                  label: 'Importación Formato de Póliza'
                }
              ]
            }
          ]
        },
        {
          key: 'calculos_anuales',
          label: 'Cálculos Anuales',
          icon: <CalculatorOutlined />,
          children: [
            {
              key: 'calculo_aguinaldo',
              label: 'Cálculo de Aguinaldo'
            },
            {
              key: 'aplicacion_aguinaldo',
              label: 'Aplicación Aguinaldo'
            },
            {
              key: 'calculo_ptu_compania',
              label: 'Cálculo PTU Compañía'
            },
            {
              key: 'calculo_ptu_global',
              label: 'Cálculo PTU Global'
            },
            {
              key: 'aplicacion_ptu',
              label: 'Aplicación PTU'
            },
            {
              key: 'reparto_ahorro',
              label: 'Reparto Ahorro'
            },
            {
              key: 'aplicacion_ahorro',
              label: 'Aplicación Ahorro'
            },
            {
              key: 'inicializacion_ahorro',
              label: 'Inicialización Ahorro'
            },
            {
              key: 'comparativo_anual_isr',
              label: 'Comparativo Anual ISR'
            },
            {
              key: 'aplicar_diferencias_isr_anual',
              label: 'Aplicar Diferencias ISR Anual'
            }
          ]
        },
        {
          key: 'reportes',
          label: 'Reportes',
          icon: <FileTextOutlined />,
          children: [
            {
              key: 'acumulados_del_periodo',
              label: 'Acumulados del Periodo'
            },
            {
              key: 'resumen_nomina_pdf',
              label: 'Resumen de Nómina'
            },
            {
              key: 'constancia_retenciones_isr',
              label: 'Constancias de Retenciones ISR'
            },
            {
              key: 'listado_de_nomina',
              label: 'Listado de Nómina'
            },
            {
              key: 'listado_de_finiquitos',
              label: 'Listado de Finiquitos'
            },
            {
              key: 'distribucion_efectivo',
              label: 'Distribución de Efectivo'
            }
          ]
        }
      ]
    },
    {
      key: 'rec',
      label: 'Recursos Humanos',
      icon: <TeamOutlined />,
      children: [
        {
          key: 'captura',
          label: 'Captura',
          icon: <FormOutlined />,
          children: [

            {
              key: 'empleados',
              label: 'Empleados',
              icon: <SolutionOutlined />,
            },
            {
              key: 'credito_infonavit',
              label: 'Crédito Infonavit',
            },
            {
              key: 'credito_fonacot',
              label: 'Crédito Fonacot',
            },
            {
              key: 'incapacidad_empleado',
              label: 'Incapacidad Empleados',
            },
            {
              key: 'timeleave',
              label: 'Tiempos de Personal',
            }
          ]
        },
        {
          key: 'imss',
          label: 'IMSS',
          icon: <SafetyCertificateOutlined />,
          children: [
            {
              key: 'sua',
              label: 'SUA',
              children: [
                {
                  key: 'sua_datos_afiltrados',
                  label: 'Datos Afiliatorios'
                },
                {
                  key: 'sua_reingresos',
                  label: 'Reingresos'
                },
                {
                  key: 'sua_movimientos',
                  label: 'Movimientos'
                },
                {
                  key: 'sua_incapacidades',
                  label: 'Incapacidades'
                },
                {
                  key: 'sua_credito_infonavit',
                  label: 'Crédito Infonavit'
                }
              ]
            },
            {
              key: 'idse',
              label: 'IDSE',
              children: [
                {
                  key: 'idse_reingresos',
                  label: 'Reingresos'
                },
                {
                  key: 'idse_bajas',
                  label: 'Bajas'
                },
                {
                  key: 'idse_cambios_salario',
                  label: 'Cambio de Salario'
                },
              ]
            }
          ]
        },
        {
          key: 'interfaces',
          label: 'Interfaces',
          icon: <ApiOutlined />,
          children: [
            {
              key: 'importacion_incapacidades',
              label: 'Importación de Incapacidades'
            },
            {
              key: 'importacion_creditos_infonavit',
              label: 'Importación de Créditos Infonavit'
            },
            {
              key: 'importacion_creditos_fonacot',
              label: 'Importación de Créditos Fonacot'
            }
          ]
        },
        {
          key: 'procesos',
          label: 'Procesos',
          icon: <ToolOutlined />,
          children: [
            {
              key: 'calculo_integrados',
              label: 'Calculo de Integrados'
            },
            {
              key: 'calculo_variable',
              label: 'Calculo de Variable'
            }
          ]
        },
        {
          key: 'reportes',
          label: 'Reportes',
          icon: <FileTextOutlined />,
          children: [
            {
              key: 'kardex_empledo',
              label: 'Kardex del Empleado'
            },
            {
              key: 'ausentismo',
              label: 'Ausentismo'
            },
          ]
        }
      ]
    },
    ...(SHOW_ASISTENCIA_MENU ? [{
      key: 'asistensia',
      label: 'Asistencia',
      icon: <CalendarOutlined />,
      children: [
        {
          key: 'empleados_asistencia',
          icon: <UserOutlined />,
          label: 'Empleados',
          children: [
            {
              key: 'rol_turno',
              label: 'Rol de Turno'
            },
            {
              key: 'autorizaciones_por_empleado',
              label: 'Autorizaciones por Empleado'
            },
                {
                  key: 'timesheet',
                  label: 'Tiempos de Trabajo'
                },
            {
              key: 'chequeo_dia',
              label: 'Checadas por Día'
            },
          ]
        },
        {
          key: 'procesos_asistencia',
          icon: <ToolOutlined />,
          label: 'Procesos',
          children: [
            {
              key: 'procesar_pre_nomina',
              label: 'Pre-Nomina Individual',
            },
            {
              key: 'procesar_pre_nomina_global',
              label: 'Pre-Nomina Global',
            },
            {
              key: 'cierre_pre_nomina',
              label: 'Cierre',
            },
            {
              key: 'desafectar_pre_nomina',
              label: 'Desafectar',
            },
            {
              key: 'autorizaciones_globales',
              label: 'Autorizaciones Globales'
            },
            {
              key: 'chequeo_entradas',
              label: 'Chequeo por Empleado'
            },
            {
              key: 'generacion_checadas',
              label: 'Generación de Checadas'
            },
            {
              key: 'borrar_checadas',
              label: 'Borrado de Checadas'
            },
          ]
        },
        {
          key: 'reportes_asistencia',
          icon: <FileTextOutlined />,
          label: 'Reportes',
          children: [
            {
              key: 'recibo_prenomina',
              label: 'Recibo Prenómina'
            },
            {
              key: 'prenomina_periodo',
              label: 'Prenómina del Periodo'
            },
            {
              key: 'reporte_tiempo_extra',
              label: 'Reporte de Tiempo Extra'
            },
            {
              key: 'reporte_tiempo_ordinario',
              label: 'Reporte de Tiempo Ordinario'
            },
          ]
        },
      ]
    }] : []),
  ];
};

export default function AdminApp() {
  const navigate = useNavigate();
  const authInfo = useSelector((state) => state.Auth);
  const { currentUser, isLoggedIn } = authInfo;
  const { visibleVistas, loading: vistasLoading } = useVisibleVistas();

  const [redirectToReferrer, setRedirectToReferrer] = React.useState(false);

  const menuOptions = React.useMemo(() => {
    if (!currentUser) return [];

    // Obtener la estructura completa del menú (igual para todos los roles)
    const fullMenu = getFullMenuStructure();

    // Filtrar por permisos basado en las vistas disponibles
    // Esto automáticamente elimina carpetas vacías
    const filteredMenu = filterMenuByPermissions(fullMenu, visibleVistas);

    return filteredMenu;
  }, [currentUser, visibleVistas]);
  // if (!isLoggedIn){
  //     return navigate("/app");
  //  }

  const menuItems = React.useMemo(
    () => formatMenuItems(menuOptions, visibleVistas),
    [menuOptions, visibleVistas]
  );

  React.useEffect(() => {
    if (isLoggedIn) {
      setRedirectToReferrer(true);
    } else {
      clearToken();
      navigate("/signin");
    }
  }, [isLoggedIn, navigate]);

  if (redirectToReferrer && !currentUser) {
    return null; // o spinner
  }

  // Mostrar spinner mientras se cargan vistas para evitar menú vacío temporal.
  if (vistasLoading && visibleVistas.size === 0) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" tip="Cargando permisos..." />
      </div>
    );
  }

  return (
    <Layout
      menuItems={menuItems}
    />
  );
}
